import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupsIcon from '@mui/icons-material/Groups';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Card,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { SECURITY_FALLBACKS } from '../../../../../../corelogic/domain/constants/displayFallbacks.constants';
import { Season } from '../../../../../../corelogic/domain/models/season.model';
import { FrontendTournamentWithDetails } from '../../../../../../corelogic/domain/models/tournament.model';
import { formatDate } from '../../../../../../corelogic/domain/utils/date.utils';
import { isFMMAPublicTournament } from '../../../../../../corelogic/domain/utils/tournament.utils';
import { SafeText } from '../../../../../../corelogic/domain/utils/xssProtection.utils';
import PulsingActiveIcon from '../../Shared/PulsingActiveIcon/PulsingActiveIcon';
import DetailsTab from './DetailsTab';
import OverallRankings from './OverallRankingsTab';
import SeasonResults from './SeasonResultsTab';
import TournamentSettingsModal from './TournamentSettingsModal';

const styles = {
  container: {
    height: '100%',
    minHeight: {
      xs: '450px',
      sm: '480px',
      md: '400px',
      lg: '600px',
      xl: '700px',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: {
      xs: 1,
      sm: 2,
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    maxWidth: 'none',
    margin: '0',
    marginTop: '15%',
    gap: 2,
    p: 2,
    bgcolor: 'rgba(20, 20, 20, 0.9)',
    borderRadius: 2,
    border: '1px solid rgba(120, 120, 120, 0.4)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
  },
  headerCard: {
    bgcolor: 'rgba(0, 0, 0, 0.98)',
    borderRadius: 2,
    border: '1px solid rgba(120, 120, 120, 0.4)',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
    p: {
      xs: 1.5,
      sm: 2.5,
    },
    mb: 1,
    minHeight: '110px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  contentCard: {
    bgcolor: 'rgba(0, 0, 0, 0.98)',
    borderRadius: 2,
    border: '1px solid rgba(120, 120, 120, 0.4)',
    boxShadow: '0 8px 15px rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  title: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(1rem, 1.4vw, 1.3rem)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    borderRadius: 1,
    px: 1,
    py: 0.5,
    ml: 2,
    fontSize: 'clamp(0.65rem, 0.9vw, 0.75rem)',
    fontWeight: 'medium',
  },
  activeBadge: {
    bgcolor: 'rgba(0, 128, 0, 0.2)',
    border: '1px solid rgba(0, 128, 0, 0.5)',
    color: '#00ff00',
    pr: 1,
  },
  inactiveBadge: {
    bgcolor: 'rgba(128, 128, 128, 0.2)',
    border: '1px solid rgba(128, 128, 128, 0.5)',
    color: '#cccccc',
    pr: 1,
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  controlButton: {
    color: '#cccccc',
    '&:hover': {
      color: '#ffffff',
      bgcolor: 'rgba(204, 0, 0, 0.2)',
    },
  },
  metaContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    mt: 1,
    minHeight: '40px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    color: '#cccccc',
    fontSize: 'clamp(0.75rem, 1.1vw, 0.85rem)',
  },
  tabsContainer: {
    bgcolor: 'rgba(0, 0, 0, 0.5)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  tabs: {
    '& .MuiTab-root': {
      color: '#cccccc',
      fontFamily: 'Arial',
      fontSize: 'clamp(0.8rem, 1vw, 0.9rem)',
      textTransform: 'none',
      minHeight: '48px',
      '&.Mui-selected': {
        color: '#ffffff',
        fontWeight: 'bold',
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#cc0000',
    },
  },
  tabContent: {
    flexGrow: 1,
    height: 'calc(100% - 49px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    bgcolor: 'rgba(0, 0, 0, 0.98)',
    borderTop: '1px solid rgba(204, 0, 0, 0.1)',
    zIndex: 1,
    position: 'relative',
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '4px',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.5)',
      },
    },
  },
  closeButton: {
    color: '#cccccc',
    '&:hover': {
      color: '#ffffff',
      bgcolor: 'rgba(204, 0, 0, 0.2)',
    },
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  detailIcon: {
    fontSize: 'small',
  },
  detailText: {
    fontSize: 'clamp(0.75rem, 1.1vw, 0.85rem)',
  },
};

interface TournamentDetailsProps {
  tournament: FrontendTournamentWithDetails | null;
  username: string;
  seasons: Season[];
  isLoading: boolean;
  onDeselectTournament: () => void;
  onLeaveTournamentResponse: (success: boolean) => void;
  onUpsertTournamentResponse: (success: boolean) => void;
}

const TournamentDetails = ({
  tournament,
  username,
  seasons,
  isLoading,
  onDeselectTournament,
  onLeaveTournamentResponse,
  onUpsertTournamentResponse,
}: TournamentDetailsProps) => {
  // States
  const [tabValue, setTabValue] = useState(0);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Reset the tab to first tabwhen tournament changes
  useEffect(() => {
    setTabValue(0);
  }, [tournament?.id]);

  const handleSettingsOpen = () => {
    setIsSettingsModalOpen(true);
  };

  const handleSettingsClose = () => {
    setIsSettingsModalOpen(false);
  };

  // Handler for part of the settings modal
  const handleLeaveTournament = (success: boolean) => {
    onLeaveTournamentResponse(success);
    if (success) {
      setIsSettingsModalOpen(false);
      onDeselectTournament();
    }
  };

  // Empty state
  if (!tournament) {
    return (
      <Box sx={styles.emptyState}>
        <EmojiEventsIcon
          sx={{ fontSize: 60, color: '#cc0000', opacity: 0.7 }}
        />
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Rubik Mono One',
            color: '#ffffff',
            textAlign: 'center',
          }}
        >
          Select a Tournament
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: '#cccccc', textAlign: 'center' }}
        >
          Choose a tournament from the list to view details and rankings
        </Typography>
      </Box>
    );
  }

  // Find available seasons for this tournament
  const availableSeasons = tournament.seasonIds
    .map((id) => seasons.find((season) => season.seasonId === id))
    .filter((season) => season !== undefined) as Season[];

  // Calculate if this is a FMMA public tournament
  const isFMMAPublic = isFMMAPublicTournament(
    tournament.name,
    tournament.creatorName,
    tournament.private,
  );

  return (
    <Box sx={styles.container}>
      {/* Header Card */}
      <Card sx={styles.headerCard} elevation={0}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={styles.title}>
              <SafeText
                text={tournament.name}
                fallback={SECURITY_FALLBACKS.TOURNAMENT_NAME}
              />
            </Typography>

            {/* Status Badge */}
            <Box
              component="span"
              sx={{
                ...styles.statusBadge,
                ...(tournament.isActive
                  ? styles.activeBadge
                  : styles.inactiveBadge),
              }}
            >
              {tournament.isActive ? (
                <>
                  <PulsingActiveIcon />
                  Active
                </>
              ) : (
                <>
                  <CheckCircleIcon sx={{ fontSize: 'small' }} />
                  Completed
                </>
              )}
            </Box>

            {/* Privacy indicator */}
            <Tooltip
              title={
                tournament.private ? 'Private Tournament' : 'Public Tournament'
              }
            >
              {tournament.private ? (
                <LockIcon sx={{ color: '#cccccc', ml: 1 }} />
              ) : (
                <PublicIcon sx={{ color: '#cccccc', ml: 1 }} />
              )}
            </Tooltip>
          </Box>

          {/* Close button */}
          <Tooltip title="Close Tournament Details">
            <IconButton
              onClick={onDeselectTournament}
              size="small"
              sx={{
                ...styles.controlButton,
                position: 'absolute',
                top: 10,
                right: 10,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Header meta container with tournament details */}
        <Box sx={styles.metaContainer}>
          <Box sx={styles.metaItem}>
            <CalendarMonthIcon fontSize="small" sx={{ color: '#cc0000' }} />
            <Typography>
              {formatDate(tournament.startDate)}
              {tournament.endDate
                ? ` - ${formatDate(tournament.endDate)}`
                : ' - TBC'}
            </Typography>
          </Box>
          <Box sx={styles.metaItem}>
            <GroupsIcon fontSize="small" sx={{ color: '#cc0000' }} />
            <Typography>
              {isFMMAPublic
                ? 'Global'
                : `${tournament.participants} ${tournament.participants === 1 ? 'Participant' : 'Participants'}`}
            </Typography>
          </Box>
          <Box sx={styles.metaItem}>
            <EmojiEventsIcon fontSize="small" sx={{ color: '#cc0000' }} />
            <Typography>
              {availableSeasons.length === 0 && isFMMAPublic
                ? 'All Seasons'
                : `${availableSeasons.length} ${availableSeasons.length === 1 ? 'Season' : 'Seasons'}`}
            </Typography>
          </Box>

          {/* Creator */}
          <Box sx={styles.detailItem}>
            <PersonIcon sx={styles.detailIcon} />
            <Typography variant="body2" sx={styles.detailText}>
              Created by:{' '}
              <SafeText
                text={isFMMAPublic ? 'Fantasy MMA' : tournament.creatorName}
                fallback={SECURITY_FALLBACKS.TOURNAMENT_CREATOR}
              />
            </Typography>
          </Box>

          {/* Settings button */}
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title="Tournament Settings">
              <IconButton
                onClick={handleSettingsOpen}
                size="small"
                sx={{
                  ...styles.controlButton,
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Card>

      {/* Content Card with tabs */}
      <Card sx={styles.contentCard} elevation={0}>
        <Box sx={styles.tabsContainer}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={styles.tabs}
            variant="fullWidth"
          >
            <Tab label="Overall Rankings" value={0} />
            <Tab label="Season Results" value={1} />
            <Tab label="Details" value={2} />
          </Tabs>
        </Box>

        <Box sx={styles.tabContent}>
          {/* Overall Rankings */}
          {tabValue === 0 && (
            <OverallRankings
              rankings={tournament.participantDetails || []}
              username={username}
              totalSeasons={tournament.numberOfSeasons}
              isCompleted={!tournament.isActive && !!tournament.endDate}
              tournamentName={tournament.name}
              tournamentCreatorName={tournament.creatorName}
              tournamentPrivate={tournament.private}
            />
          )}

          {/* Season Results */}
          {tabValue === 1 && (
            <SeasonResults
              seasons={availableSeasons}
              participants={tournament.participantDetails || []}
              username={username}
              isFMMAPublic={isFMMAPublic}
            />
          )}

          {/* Details Tab */}
          {tabValue === 2 && (
            <DetailsTab
              tournament={tournament}
              availableSeasons={availableSeasons}
            />
          )}
        </Box>
      </Card>

      {/* Settings Modal */}
      <TournamentSettingsModal
        open={isSettingsModalOpen}
        onClose={handleSettingsClose}
        tournament={tournament}
        isCreator={tournament.creatorName === username}
        onLeaveTournament={handleLeaveTournament}
        onUpsertTournament={onUpsertTournamentResponse}
      />
    </Box>
  );
};

export default TournamentDetails;
